import React, { useEffect, useRef, useState } from 'react'
import { BarChart3, AlertTriangle } from 'lucide-react'
import { parseChartSpec } from './chartSpec'
import ChartRenderer from './ChartRenderer'

/** Measure an element's width via ResizeObserver. We measure ourselves (rather than using
 *  Nivo's Responsive* wrappers) so charts render reliably under React StrictMode. */
const useElementWidth = (): [React.RefObject<HTMLDivElement | null>, number] => {
  const ref = useRef<HTMLDivElement | null>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width ?? 0
      if (w > 0) setWidth(w)
    })
    observer.observe(el)
    setWidth(el.clientWidth)
    return () => observer.disconnect()
  }, [])
  return [ref, width]
}

// Catch Nivo render errors so a malformed spec degrades gracefully instead of blanking
// the whole message.
class ChartErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { failed: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { failed: false }
  }
  static getDerivedStateFromError() {
    return { failed: true }
  }
  render() {
    if (this.state.failed) {
      return (
        <div className="chart-block__note chart-block__note--error">
          <AlertTriangle size={14} />
          Could not render this chart.
        </div>
      )
    }
    return this.props.children
  }
}

/** Renders a ```chart fenced block. `code` is the raw block body (may be partial while the
 *  response is still streaming). */
const ChartBlock: React.FC<{ code: string }> = ({ code }) => {
  const [canvasRef, width] = useElementWidth()
  const { spec, error } = parseChartSpec(code)

  // Still streaming (JSON not yet complete) — show a skeleton, not an error.
  if (!spec && !error) {
    return (
      <div className="chart-block chart-block--loading">
        <BarChart3 size={16} className="chart-block__spinner-icon" />
        Building chart…
      </div>
    )
  }

  if (error || !spec) {
    return (
      <div className="chart-block__note chart-block__note--error">
        <AlertTriangle size={14} />
        {error ?? 'Invalid chart.'}
      </div>
    )
  }

  const height = spec.height ?? 320

  return (
    <div className="chart-block">
      {spec.title && <div className="chart-block__title">{spec.title}</div>}
      <div ref={canvasRef} className="chart-block__canvas" style={{ height }}>
        {width > 0 && (
          <ChartErrorBoundary>
            <ChartRenderer spec={spec} width={width} height={height} />
          </ChartErrorBoundary>
        )}
      </div>
    </div>
  )
}

export default ChartBlock
